import { useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'

import {
  AssignedProperty,
  AssignedPropertyValue,
  Assignee,
  AssigneeType,
  DefaultType,
  PostStatusPropertyValue,
  Property,
  PropertyClient,
  PropertyValue,
} from 'api/clients/PropertyClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { convertPropertyValuesToAssignedPropertyValues } from 'common/PropertyValue/utils'
import { createAssignedPropertyValue } from 'screens/Finder/components/FileProperty/FileProperty'
import { upsertFilesProperties } from 'redux/actions/filesProperties'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useProperties } from 'hooks/useProperties'
import { usePostPreviewContext } from 'components/PostPreviewModal/PostPreviewContext'
import { useLoadAssignedProperties } from 'hooks/useLoadAssignedProperties'
import { useRootStore } from 'stores/RootStore'
import { fileToAssignee } from 'screens/Finder/Utils'
import { usePostPreviewStore } from '../PostPreviewStoreProvider'

export function usePostPropertyMutations() {
  const workspace = useCurrentWorkspace()
  const workspaceId = useCurrentWorkspaceId()
  const dispatch = useDispatch()
  const { fileId } = usePostPreviewContext()
  const fileStore = useRootStore().files
  const { target } = usePostPreviewStore()

  const file = fileStore.get(fileId)

  const projectId = file?.parent?.id

  const { currentFolderProperties, currentWorkspaceProperties, projectProperties } = useProperties()
  const postFolderProperties = projectId ? projectProperties(projectId) : []
  const projectPostAtProperty = postFolderProperties.find((p) => p.name === 'Post Date & Time')

  const folderPostAtProperty = currentFolderProperties.find((p) => p.name === 'Post Date & Time')
  const postAtProperty = projectPostAtProperty || folderPostAtProperty
  const postStatusProperty = currentWorkspaceProperties.find((p) => p.default_type === DefaultType.PostStatus)

  const { loadAssignedProperties } = useLoadAssignedProperties()

  useEffect(() => {
    if (!file) return

    const propertyIds: number[] = []
    if (postStatusProperty) propertyIds.push(postStatusProperty.id)
    if (postAtProperty) propertyIds.push(postAtProperty.id)

    if (propertyIds.length) {
      loadAssignedProperties([file], propertyIds)
    }
  }, [file, postStatusProperty, postAtProperty, loadAssignedProperties])

  const findOrCreateAssignedProperty = useCallback(async (assignee: Assignee, property: Property) => {
    const res = await PropertyClient.findOrCreateAssignedProperty<AssignedProperty>(
      assignee.id,
      property,
      assignee.type,
    )
    return res.data.assigned_property
  }, [])

  const createPropertyValue = useCallback(
    async ({ value, property_id }: { value: any; property_id: number }) => {
      if (!workspace) return null
      const res = await PropertyClient.createPropertyValue(workspace.key, {
        value,
        property_id,
      } as PropertyValue)
      return res?.type === SUCCESS_RESPONSE_TYPE ? res.data.property_value : null
    },
    [workspace],
  )

  const updateAssignedProperty = useCallback(
    async (assignedPropertyId: number, property: Property, assignedPropertyValues: AssignedPropertyValue[]) => {
      const res = await PropertyClient.updateAssignedProperty<AssignedProperty>(
        assignedPropertyId,
        property,
        assignedPropertyValues,
      )
      if (res?.type === SUCCESS_RESPONSE_TYPE) {
        dispatch(upsertFilesProperties([res.data.assigned_property]))
      }
    },
    [dispatch],
  )

  const updateStatus = useCallback(
    async (fileId: string, status: PostStatusPropertyValue) => {
      if (!postStatusProperty) return

      const statusValue = postStatusProperty.property_values.find((pv) => pv.value === status)
      if (!statusValue) return

      const assignee: Assignee =
        file && fileId === file.id
          ? fileToAssignee(file)
          : { id: fileId, type: target === 'design' ? AssigneeType.Design : AssigneeType.Article }

      const assignedProperty = await findOrCreateAssignedProperty(assignee, postStatusProperty)

      const assignedValues = convertPropertyValuesToAssignedPropertyValues(postStatusProperty, [
        statusValue,
      ]) as AssignedPropertyValue[]

      await updateAssignedProperty(assignedProperty.id, postStatusProperty, assignedValues)
    },
    [postStatusProperty, findOrCreateAssignedProperty, updateAssignedProperty, file, target],
  )

  const updatePostAt = useCallback(
    async (fileId: string, isoDate: string | null) => {
      if (!postAtProperty) return

      const assignee: Assignee =
        file && fileId === file.id
          ? fileToAssignee(file)
          : { id: fileId, type: target === 'design' ? AssigneeType.Design : AssigneeType.Article }

      const assignedProperty = await findOrCreateAssignedProperty(assignee, postAtProperty)

      const existingValue = assignedProperty.property_values?.[0]
      if (existingValue) {
        const res = await PropertyClient.updatePropertyValue(workspaceId.toString(), {
          ...existingValue,
          value: isoDate,
        } as PropertyValue)

        if (res?.type === SUCCESS_RESPONSE_TYPE) {
          dispatch(upsertFilesProperties([assignedProperty]))
        }
        return
      }

      const newPropertyValue = await createPropertyValue({
        value: isoDate,
        property_id: postAtProperty.id,
      })
      if (!newPropertyValue) return

      const res = await createAssignedPropertyValue(assignedProperty.id, postAtProperty, [newPropertyValue])
      if (res?.type === SUCCESS_RESPONSE_TYPE) {
        dispatch(upsertFilesProperties([res.data.assigned_property]))
      }
    },
    [postAtProperty, workspaceId, findOrCreateAssignedProperty, createPropertyValue, dispatch, file, target],
  )

  return { updateStatus, updatePostAt }
}
